import { Component, Input, OnInit } from '@angular/core';
import { ConfirmationService, LazyLoadEvent, MessageService, SelectItem } from 'primeng';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { IPageResult } from 'src/app/interfaces/page-result';
import { IServiceFeeType, IServiceFeeTypeFilter } from 'src/app/interfaces/service-fee';
import { ServiceFeeTypeService } from 'src/app/services/service-fee-type-service';

@Component({
  selector: 'app-service-fee-type',
  templateUrl: './service-fee-type.component.html',
  styleUrls: ['./service-fee-type.component.css'],
})
export class ServiceFeeTypeComponent implements OnInit {
  @Input() isReadonly: boolean;

  public serviceFeeTypes: IPageResult<IServiceFeeType>;
  public filter: Subject<IServiceFeeTypeFilter>;
  public filterVal: IServiceFeeTypeFilter;
  public isBusy: boolean = false;
  public serviceFeeType: IServiceFeeType;
  public clonedServiceFeeTypes: { [s: string]: IServiceFeeType } = {};
  public cols = [];
  public selectedServiceFeeType: IServiceFeeType;
  public displayDialog: boolean = false;
  public newServiceFeeType: boolean = false;
  public serviceFeeTypeList: IServiceFeeType[];
  constructor(
    private serviceFeeTypeService: ServiceFeeTypeService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.serviceFeeType = <IServiceFeeType>{};
    this.initDefaultFilter();
    this.filter = new Subject<IServiceFeeTypeFilter>();
    this.filter.pipe(debounceTime(500)).subscribe((f) => {
      this.loadServiceFeeTypes(f);
      this.filterVal = f;
    });
    this.filter.next(this.filterVal);
    this.cols = [
      { field: 'name', header: 'Name' },
      { field: 'displayName', header: 'Display Name' },
      { field: 'defaultValue', header: 'Default Value' },
    ];
  }

  initDefaultFilter() {
    this.filterVal = {
      first: 0,
      rows: 10,
      sortField: 'Id', // ASC
      sortOrder: 1,
      filters: {},
    };
  }

  onGridLazyLoad(event: LazyLoadEvent) {
    this.filterVal.first = event.first;
    this.filterVal.rows = event.rows;
    this.filterVal.sortField = event.sortField;
    this.filterVal.sortOrder = event.sortOrder;

    this.filter.next(this.filterVal);
  }

  loadServiceFeeTypes(filterCommand: IServiceFeeTypeFilter) {
    this.serviceFeeTypeService
      .getPage(filterCommand)
      .then((res) => {
        this.serviceFeeTypes = res;
      })
      .catch((err) => {
        console.log('An error occurred loading Service Fee Types');
      })
      .finally();
  }

  showDialogToAdd() {
    this.newServiceFeeType = true;
    this.serviceFeeType = <IServiceFeeType>{};
    this.displayDialog = true;
  }

  onRowSelect(event) {
    if (this.isReadonly) {
      return;
    }

    this.newServiceFeeType = false;
    this.serviceFeeType = this.cloneServiceFeeType(event.data);
    this.displayDialog = true;
  }

  cloneServiceFeeType(c: IServiceFeeType): IServiceFeeType {
    let customerServiceFee = <IServiceFeeType>{};
    for (let prop in c) {
      customerServiceFee[prop] = c[prop];
    }
    return customerServiceFee;
  }

  save() {
    if (!this.serviceFeeType.name) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Service Name is required',
      });
      return;
    }
    if (!this.serviceFeeType.defaultValue) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Default Fee Value is required',
      });
    }
    if (this.serviceFeeType.id) {
      this.serviceFeeTypeService
        .update(this.serviceFeeType.id, this.serviceFeeType)
        .then((res) => {
          this.serviceFeeType = null;
          this.displayDialog = false;
          this.filter.next(this.filterVal);
        })
        .catch((err) => {
          console.log('An error occurred updating Service Fee Type');
        })
        .finally();
    } else {
      this.serviceFeeTypeService
        .create(this.serviceFeeType)
        .then((res) => {
          this.serviceFeeType = null;
          this.displayDialog = false;
          this.filter.next(this.filterVal);
        })
        .catch((err) => {
          console.log('An error occurred creating Service Fee Type');
        })
        .finally();
    }
  }

  delete() {
    this.confirmationService.confirm({
      message:
        'Are you sure that you want to delete this service type? You will need to ensure existing associations are removed first',
      accept: () => {
        this.serviceFeeTypeService
          .delete(this.serviceFeeType.id)
          .then((res) => {
            this.serviceFeeType = null;
            this.displayDialog = false;
            this.filter.next(this.filterVal);
          })
          .catch((err) => {
            console.log('An error occurred deleting Service Fee Type');
          })
          .finally();
      },
      reject: () => {},
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      icon: 'pi pi-exclamation-triangle',
    });
  }
}
